import { SurveyType } from '@commutifi/models/Surveys'
import { HttpStatusCode } from '@commutifi-fe/constants'
import { FormattedMessage } from 'locales/index'
import React from 'react'
import { connect } from 'react-redux'
import { Redirect, RouteComponentProps } from 'react-router-dom'
import { Button as AppButton, GenericError, ErrorBoundary } from '@commutifi-fe/ui'
import { Question } from '@commutifi-fe/interfaces'
import { CommuterRouting, EnterpriseSurveyPathsParams } from 'src/routing'
import { selectors } from 'store/modules/enterpriseSurvey'
import { RootState } from 'store/index'
import storage from 'utils/storage'
import Steps from 'views/Anonymous/views/Onboarding/SurveyForm/Steps'
import SurveyLayout from '../layout/SurveyLayout'
import SectionForm from './SectionForm'
import { FollowUpStorageKeys } from 'constants/onboarding'

interface ReduxProps {
  //  Redux state props
  questionsBySection: Record<string, Question[]>
  orderedSections: number[]
  completionPercentage: number
  surveyType: SurveyType
}

interface OwnProps {
  /**
   * Callback when all survey steps are completed
   * and user clicks on 'Complete' button
   */
  onSurveyFinish: () => void
  isLoading?: boolean
  submitButtonText?: React.ReactNode // Ideally a string or FormattedMassage component
  children?: (currentStep: number) => React.ReactNode
}

export type RouteProps = RouteComponentProps<{ [EnterpriseSurveyPathsParams.Step]: string }, any, { referrer?: string }>
export type SurveyFormProps = RouteProps & ReduxProps & OwnProps

const START_STEP = 1

function SurveyForm({
  questionsBySection,
  orderedSections,
  completionPercentage,
  surveyType,
  isLoading,
  submitButtonText,
  history,
  match,
  location,
  children,
  onSurveyFinish
}: SurveyFormProps) {
  const currentStep = React.useMemo(() => {
    const matchedStep = /\d+$/.exec(match.params.step || '')
    return matchedStep ? parseInt(matchedStep[0]) : START_STEP
  }, [match.params.step])
  const totalSteps = React.useMemo(() => orderedSections.length, [orderedSections])

  React.useEffect(() => {
    // there’s an edge-case that we want to support: a survey with conditional questions
    // (e.g. a follow-up only for users taking the bus), in which case some users won’t see any questions
    if (totalSteps === 0 && surveyType === SurveyType.followUp) {
      Object.values(FollowUpStorageKeys).forEach((storageKey) => storage.remove(storageKey))
      return history.push(CommuterRouting.dashboard.path)
    }
  }, [history, surveyType, totalSteps])

  if (totalSteps === 0) {
    return <GenericError error={{ status: HttpStatusCode.NotFound }} />
  }

  // Prevent the user to access steps that don't exists
  if (currentStep > totalSteps) {
    return <Redirect to={`${EnterpriseSurveyPathsParams.Step}1`} />
  }

  const nextStep = () => {
    const nextStep = currentStep + 1
    history.push(`./${EnterpriseSurveyPathsParams.Step}${nextStep}`)
  }
  const setStep = (step: number) => {
    history.push(`./${EnterpriseSurveyPathsParams.Step}${step}`)
  }

  const handleStepCompleted = () => {
    if (totalSteps === currentStep) {
      onSurveyFinish()
    } else {
      nextStep()
    }
  }

  const handleStepsChange = (step: number) => {
    setStep(step)
  }

  const handleReturnToLandingPage = () => {
    storage.clear()
    history.push('../')
  }

  return (
    <SurveyLayout>
      <div className="u-flex-1 u-flex-justify-center u-padding-top--large u-bg-color--layout">
        <Steps current={currentStep} onChange={handleStepsChange} total={totalSteps} progress={false}>
          {orderedSections.map((section, index) => {
            const currentSectionNumber = index + 1
            return (
              <Steps.Step key={section} title={<FormattedMessage id="onboarding.steps.button.previous" />}>
                <ErrorBoundary
                  ErrorComponent={GenericError}
                  actions={
                    <AppButton onClick={handleReturnToLandingPage}>
                      <FormattedMessage id="onboarding.errors.button.returnHome" />
                    </AppButton>
                  }
                >
                  {children ? children(currentStep) : null}
                  <SectionForm
                    // We pass down the questions for a section even if SectionForm is a connected to the store
                    // because we need the section count to show progress anyway.
                    initialValidation={Boolean(
                      location.state?.referrer && location.state.referrer.endsWith('/register')
                    )}
                    questions={questionsBySection[section]}
                    currentStep={section}
                    completionPercentage={completionPercentage}
                    nextSectionText={
                      totalSteps === currentSectionNumber ? (
                        submitButtonText || <FormattedMessage id="global.submit" />
                      ) : (
                        <FormattedMessage id="onboarding.steps.button.next" />
                      )
                    }
                    onNextSection={handleStepCompleted}
                    isLoading={isLoading}
                  />
                </ErrorBoundary>
              </Steps.Step>
            )
          })}
        </Steps>
      </div>
    </SurveyLayout>
  )
}

const mapStateToProps = (state: RootState) => ({
  questionsBySection: selectors.getQuestionsBySectionWithConditionals(state) as Record<string, Question[]>,
  orderedSections: selectors.getOrderedSections(state),
  completionPercentage: selectors.getPercentageOfCompletion(state),
  surveyType: selectors.makeGetSurvey('type')(state)
})

export const SurveyFormContainer = connect<ReduxProps, unknown, OwnProps>(mapStateToProps, null)(SurveyForm)
export default SurveyFormContainer
